import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Session } from '@supabase/supabase-js';
import { Button } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import type { AvailabilityKey, EngineTypeKey, StatusKey } from '../features/planning/types';
import { useProjects } from '../features/planning/hooks/useProjects';
import { useMachines } from '../features/planning/hooks/useMachines';
import { useOrgFeatures } from '../features/planning/hooks/useOrgFeatures';
import { useExportBookings } from '../features/planning/hooks/useExportBookings';
import { useImportMachines } from '../features/planning/hooks/useImportMachines';
import { useDashboardCounts } from '../features/dashboard/hooks/useDashboardCounts';
import {
  useDashboardChartData,
  CATEGORY_OTHER_ID,
} from '../features/dashboard/hooks/useDashboardChartData';
import { useExtensionSettings } from '../features/dashboard/hooks/useExtensionSettings';
import { useDashboardNavigation } from '../features/dashboard/hooks/useDashboardNavigation';
import { getChartTheme } from '../features/dashboard/utils/chartTheme';
import StatDayCard from '../features/dashboard/components/StatDayCard';
import ChartCard from '../features/dashboard/components/ChartCard';
import ExtensionSuggestionCard from '../features/dashboard/components/ExtensionSuggestionCard';
import OverdueReturnStatCard from '../features/dashboard/components/OverdueReturnStatCard';
import MachinePieChart from '../features/dashboard/components/MachinePieChart';
import SiteOccupancyChart from '../features/dashboard/components/SiteOccupancyChart';
import SubcontractorChart from '../features/dashboard/components/SubcontractorChart';
import TimelineChart from '../features/dashboard/components/TimelineChart';
import ImportMachinesDialog from '../features/dashboard/components/ImportMachinesDialog';
import PageHeader from '../components/PageHeader';
import Spinner from '../components/Spinner';
import { useNotification } from '../context/useNotification';
import type { ExtensionSettings } from '../features/dashboard/types';
import { DASHBOARD_LABELS as LABELS, EXPORT_LABELS, IMPORT_LABELS } from '../utils/labels';
import { appNow } from '../utils/timezone';
import { hasFullAccess } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface DashboardPageProps {
  session: Session;
  orgId: string | null;
  selectedProjectId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

export default function DashboardPage({ session, orgId, selectedProjectId, isSuperAdmin, role }: DashboardPageProps) {
  const navigate = useNavigate();
  const canImport = hasFullAccess(role, isSuperAdmin);
  const userId = session.user.id;
  const isMobile = useMediaQuery('(max-width:1023px)');
  const { projects, loading: projectsLoading } = useProjects(orgId);
  const { mode } = useColorScheme();
  const isDark = mode === 'dark';
  const [importOpen, setImportOpen] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const { settings: extensionSettings, updateSettings: setExtensionSettings } =
    useExtensionSettings(userId);

  useEffect(() => {
    if (isMobile) navigate('/planning', { replace: true });
  }, [isMobile, navigate]);

  const validProjectId = projects.some((p) => p.id === selectedProjectId)
    ? selectedProjectId
    : null;
  const currentProjectId = validProjectId ?? projects[0]?.id ?? null;
  const currentProject = projects.find((p) => p.id === currentProjectId);

  useEffect(() => {
    setCategoriesExpanded(false);
  }, [currentProjectId]);

  const today = useMemo(() => appNow().format('YYYY-MM-DD'), []);
  const tomorrow = useMemo(() => appNow().add(1, 'day').format('YYYY-MM-DD'), []);
  // Same offset as the "Today" button in planning: today lands on the 4th column
  const gridViewStart = useMemo(() => appNow().subtract(3, 'day').format('YYYY-MM-DD'), []);

  const { machines, loading: machinesLoading } = useMachines(currentProjectId, orgId);
  const { pickupHandinEnabled } = useOrgFeatures(orgId);
  const { exportBookings, exporting: exportingBookings } = useExportBookings(currentProjectId);
  const importState = useImportMachines(currentProjectId);
  const { notifySuccess, notifyError } = useNotification();

  const handleExportBookings = () => {
    exportBookings()
      .then(() => notifySuccess(EXPORT_LABELS.exportSuccess))
      .catch(() => notifyError(EXPORT_LABELS.exportError));
  };

  const handleDownloadTemplate = () => {
    importState
      .downloadTemplate()
      .then(() => notifySuccess(IMPORT_LABELS.downloadSuccess))
      .catch(() => notifyError(IMPORT_LABELS.downloadError));
  };

  const handleImportMachines = (file: File) => {
    importState
      .importMachines(file)
      .then((result) => {
        if (result.errors.length === 0) notifySuccess(IMPORT_LABELS.importSuccess(result.added));
      })
      .catch(() => notifyError(IMPORT_LABELS.importError));
  };

  const handleExtensionSettingsChange = (next: ExtensionSettings) => {
    setExtensionSettings(next).then((ok) => {
      if (ok) notifySuccess(LABELS.extensionSaveSuccess);
      else notifyError(LABELS.extensionSaveError);
    });
  };

  const counts = useDashboardCounts(machines, today, tomorrow);
  const chartData = useDashboardChartData(
    machines,
    isDark,
    today,
    categoriesExpanded,
    extensionSettings,
    pickupHandinEnabled
  );
  const chartTheme = getChartTheme(isDark);
  const {
    navigateWithAvailability,
    navigateWithStatus,
    navigateWithEngineType,
    navigateWithCategory,
    navigateWithSite,
    navigateWithRentalExtension,
    navigateWithOverdueReturn,
    navigateWithDashboardFilter,
  } = useDashboardNavigation(gridViewStart);

  if (projectsLoading && projects.length === 0) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <Spinner size="lg" className="text-primary" />
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />

      <ImportMachinesDialog
        open={importOpen}
        onClose={() => { setImportOpen(false); importState.resetImport(); }}
        projectId={currentProjectId}
        {...importState}
        downloadTemplate={handleDownloadTemplate}
        importMachines={handleImportMachines}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="font-bold mb-0 mt-0">{LABELS.title}</h1>
              {currentProject && (
                <p className="text-text-secondary mt-0 mb-0">
                  {currentProject.name} ({currentProject.year})
                </p>
              )}
            </div>
            {canImport && (
              <Button
                variant="outlined"
                startIcon={<FileUploadOutlinedIcon />}
                onClick={() => setImportOpen(true)}
                disabled={!currentProjectId}
                data-testid="import-button"
              >
                {LABELS.importButton}
              </Button>
            )}
          </div>

          {!currentProjectId && <p className="text-text-secondary">{LABELS.noProject}</p>}

          {currentProjectId && machinesLoading && (
            <div className="flex justify-center py-12">
              <Spinner size="lg" className="text-primary" />
            </div>
          )}

          {currentProjectId && !machinesLoading && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatDayCard
                  icon={<ShoppingCartOutlinedIcon color="warning" />}
                  title={LABELS.cardToOrder}
                  infoDescription={LABELS.infoToOrder}
                  todayValue={counts.toOrderToday}
                  tomorrowValue={counts.toOrderTomorrow}
                  warn
                  onNavigate={() => navigateWithDashboardFilter('to_order_action')}
                  testId="stat-card-to-order"
                />
                <StatDayCard
                  icon={<ArrowCircleDownOutlinedIcon color="primary" />}
                  title={LABELS.cardArriving}
                  infoDescription={LABELS.infoArriving}
                  todayValue={counts.arrivingToday}
                  tomorrowValue={counts.arrivingTomorrow}
                  onNavigate={() => navigateWithDashboardFilter('arriving')}
                  testId="stat-card-arriving"
                />
                <StatDayCard
                  icon={<ArrowCircleUpOutlinedIcon color="success" />}
                  title={LABELS.cardPickup}
                  infoDescription={LABELS.infoPickup}
                  todayValue={counts.pickupToday}
                  tomorrowValue={counts.pickupTomorrow}
                  onNavigate={() => navigateWithDashboardFilter('pickup_confirmed')}
                  testId="stat-card-pickup"
                />
                <StatDayCard
                  icon={<WarningAmberIcon color="warning" />}
                  title={LABELS.cardAction}
                  infoDescription={LABELS.infoAction}
                  todayValue={counts.actionToday}
                  tomorrowValue={counts.actionTomorrow}
                  warn
                  onNavigate={() => navigateWithDashboardFilter('pickup_action')}
                  testId="stat-card-action"
                />
              </div>
              <ExtensionSuggestionCard
                suggestions={chartData.extensionSuggestions}
                onNavigate={navigateWithRentalExtension}
                settings={extensionSettings}
                onSettingsChange={handleExtensionSettingsChange}
                userId={userId}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ChartCard
                  title={LABELS.machineStatusTitle}
                  isEmpty={chartData.statusChartData.length === 0}
                  testId="chart-card-status"
                >
                  <MachinePieChart
                    data={chartData.statusChartData}
                    ariaLabel={LABELS.machineStatusTitle}
                    chartTheme={chartTheme}
                    onSelect={(key) => navigateWithStatus(key as StatusKey)}
                    testId="chart-status"
                  />
                </ChartCard>

                <ChartCard
                  title={LABELS.machineEngineTypeTitle}
                  isEmpty={chartData.engineTypeChartData.length === 0}
                  testId="chart-card-engine-type"
                >
                  <MachinePieChart
                    data={chartData.engineTypeChartData}
                    ariaLabel={LABELS.machineEngineTypeTitle}
                    chartTheme={chartTheme}
                    onSelect={(key) => navigateWithEngineType(key as EngineTypeKey)}
                    testId="chart-engine-type"
                  />
                </ChartCard>
              </div>

              <ChartCard
                title={LABELS.machineCategoryTitle}
                isEmpty={chartData.categoryChartData.length === 0}
                testId="chart-card-category"
              >
                <SiteOccupancyChart
                  data={chartData.categoryChartData}
                  ariaLabel={LABELS.machineCategoryTitle}
                  chartTheme={chartTheme}
                  onSelect={(id) =>
                    id === CATEGORY_OTHER_ID
                      ? setCategoriesExpanded(true)
                      : navigateWithCategory(id)
                  }
                />
              </ChartCard>

              <ChartCard
                title={LABELS.siteOccupancyTitle}
                isEmpty={chartData.siteOccupancyData.length === 0}
                testId="chart-card-site"
              >
                <SiteOccupancyChart
                  data={chartData.siteOccupancyData}
                  ariaLabel={LABELS.siteOccupancyTitle}
                  chartTheme={chartTheme}
                  onSelect={navigateWithSite}
                />
              </ChartCard>

              {(() => {
                const availabilityChart = (
                  <ChartCard
                    title={LABELS.machineAvailabilityTitle}
                    isEmpty={chartData.availabilityChartData.length === 0}
                    testId="chart-card-availability"
                  >
                    <MachinePieChart
                      data={chartData.availabilityChartData}
                      ariaLabel={LABELS.machineAvailabilityTitle}
                      chartTheme={chartTheme}
                      onSelect={(key) => navigateWithAvailability(key as AvailabilityKey)}
                      testId="chart-availability"
                    />
                  </ChartCard>
                );
                const subcontractorChart = (
                  <ChartCard
                    title={LABELS.subcontractorTitle}
                    isEmpty={chartData.subcontractorData.length === 0}
                    onDownload={handleExportBookings}
                    downloadLoading={exportingBookings}
                    downloadLabel={LABELS.downloadBookingsLabel}
                    testId="chart-card-subcontractor"
                  >
                    <SubcontractorChart
                      data={chartData.subcontractorData}
                      chartTheme={chartTheme}
                      isDark={isDark}
                    />
                  </ChartCard>
                );

                // With pickup/hand-in off, the overdue-return chart never renders — pairing
                // the availability chart with the subcontractor chart instead avoids a pie
                // chart sitting alone in a full-width row (lots of empty whitespace beside it).
                if (!pickupHandinEnabled) {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {availabilityChart}
                      {subcontractorChart}
                    </div>
                  );
                }

                const overdueCount = chartData.overdueReturnBookings.length;
                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {availabilityChart}
                      <ChartCard
                        title={overdueCount === 0 ? LABELS.overdueReturnAllOnTime : LABELS.overdueReturnSummary(overdueCount)}
                        icon={overdueCount === 0
                          ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                          : <WarningAmberIcon fontSize="small" color="error" />}
                        isEmpty={false}
                        testId="chart-card-overdue-return"
                      >
                        <OverdueReturnStatCard
                          overdueBookings={chartData.overdueReturnBookings}
                          onNavigate={navigateWithOverdueReturn}
                        />
                      </ChartCard>
                    </div>
                    {subcontractorChart}
                  </>
                );
              })()}

              <ChartCard
                title={LABELS.timelineTitle}
                isEmpty={chartData.timelineData.every((d) => d.aankomsten === 0 && d.retours === 0)}
                testId="chart-card-timeline"
              >
                <TimelineChart
                  data={chartData.timelineData}
                  chartTheme={chartTheme}
                  isDark={isDark}
                />
              </ChartCard>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
